import {
  Anchor,
  Button,
  InputWrapper,
  SimpleGrid,
  Text,
  Textarea,
} from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { useForm } from '@mantine/form'
import type {
  Attachment,
  Patient,
  Practitioner,
  Reference,
  RelatedPerson,
} from '@medplum/fhirtypes'
import { ReferenceInput } from '@medplum/react'
import { invariant } from '@seen/core/invariant'
import { CommunicationMedium } from '@seen/core/models/communications/constants'
import { SDRNotificationCommunication } from '@seen/core/models/communications/SDRNotificationCommunication'
import { DocumentCategory } from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { createReference } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { type JSX, useCallback, useState } from 'react'
import { CustomAttachmentButton } from '#src/components/button/CustomAttachmentButton.tsx'
import { SDRRequesterInput } from '#src/components/sdr/create/SDRRequesterInput.tsx'
import type { SDRRequesterState } from '#src/components/sdr/create/utils.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

type FormData = {
  sender: Reference<Practitioner> | null
  recipient: SDRRequesterState | null
  notificationDate: Date
  notes: string
  documents: Attachment[]
}

type Props = {
  sdr: SDR
  patient: Patient
  medium: CommunicationMedium
  onCancel: () => void
}

export function SDRCreateNotificationAttempt({
  sdr,
  patient,
  medium,
  onCancel,
}: Props): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const [uploadingStatus, setUploadingStatus] = useState<
    'idle' | 'uploading' | 'error'
  >('idle')

  const practitionerRef = useCurrentPractitioner().reference
  let initialRecipient: SDRRequesterState = { type: 'participant' }
  if (sdr.participantRequester) {
    initialRecipient = { type: 'participant' }
  } else if (sdr.relatedPersonRequester) {
    initialRecipient = {
      type: 'relatedPerson',
      reference: sdr.relatedPersonRequester,
    }
  }
  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      sender: practitionerRef,
      recipient: initialRecipient,
      notificationDate: new Date(),
      notes: '',
      documents: [],
    },

    validate: {
      sender: (value) => (value != null ? null : 'Sender is required'),
      recipient: (value) => (value != null ? null : 'Recipient is required'),
      notificationDate: (value) =>
        value != null ? null : 'Notification date is required',
      notes: (value) => (value.length > 0 ? null : 'Notes are required'),
      documents: (value) =>
        medium === CommunicationMedium.VERBAL || value.length > 0
          ? null
          : 'Documents are required for written notifications',
    },
  })

  const {
    isError: isTransactionError,
    isPending: isTransactionPending,
    mutate: mutateTransaction,
  } = useTransactionMutation()

  const handleCreate = useCallback(
    async (formData: FormData) => {
      const description = `SDR Notification Document "${sdr.title ?? sdr.description}"`
      const newDocRefs: ExtendedDocumentReference[] = formData.documents.map(
        (attachment) =>
          new ExtendedDocumentReference({
            subject: createReference(patient),
            author: practitionerRef,
            attachments: [attachment],
            description,
            categories: [DocumentCategory.SDR],
            context: {
              related: [sdr.reference],
            },
          })
      )

      let recipientReference: Reference<Patient> | Reference<RelatedPerson>
      invariant(
        formData.recipient != null &&
          formData.recipient.type !== 'newRelatedPerson',
        'Recipient must be specified and cannot be a new related person'
      )
      switch (formData.recipient.type) {
        case 'participant':
          recipientReference = createReference(patient)
          break
        case 'relatedPerson':
          recipientReference = formData.recipient.reference
          break
      }

      const newCommunication = new SDRNotificationCommunication({
        sender: formData.sender ?? practitionerRef,
        recipient: [recipientReference],
        sent: formData.notificationDate.toISOString(),
        message: formData.notes,
        partOf: [sdr.reference],
      })
      if (isOfType(recipientReference, 'Patient')) {
        newCommunication.subject = recipientReference
      }
      newCommunication.medium = medium

      mutateTransaction(
        (builder) => {
          const docRefs = newDocRefs.map((docRef) => builder.create(docRef))
          docRefs.forEach((docRef) => {
            newCommunication.addDocumentPayload(docRef)
          })
          const communication = builder.create(newCommunication)
          builder.patch(sdr, (sdr) => {
            if (medium === CommunicationMedium.VERBAL) {
              sdr.addVerbalNotificationAttempt(communication)
            } else if (medium === CommunicationMedium.WRITTEN) {
              sdr.addWrittenNotificationAttempt(communication)
            }
          })
        },
        {
          onSuccess: () => {
            onCancel()
          },
        }
      )
    },
    [medium, onCancel, patient, practitionerRef, sdr, mutateTransaction]
  )

  return (
    <form
      onSubmit={form.onSubmit(handleCreate, (err) => {
        console.error('Error:', err)
      })}
    >
      <div className="flex flex-col gap-4">
        <Text className="text-lg font-semibold">Notification</Text>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {/* select for reporter */}
          {/* Care team selector */}
          <InputWrapper label="Reported by" required>
            <ReferenceInput
              name="sender"
              targetTypes={['Practitioner']}
              key={form.key('sender')}
              {...form.getInputProps('sender')}
            />
          </InputWrapper>

          <SDRRequesterInput
            patient={patient}
            required
            label="Person Notified"
            allowCreation={false}
            key={form.key('recipient')}
            {...form.getInputProps('recipient')}
          />

          <DateTimePicker
            label="Notification Date"
            valueFormat="DD MMM YYYY hh:mm A"
            required
            key={form.key('notificationDate')}
            {...form.getInputProps('notificationDate')}
          />
        </SimpleGrid>

        <Textarea
          label="Notes"
          placeholder="Enter notes here"
          required
          key={form.key('notes')}
          {...form.getInputProps('notes')}
        />

        {medium === CommunicationMedium.WRITTEN && (
          <InputWrapper
            required
            label="Attach copy of letter/written notification"
            className="flex flex-col gap-2"
          >
            <div>
              <CustomAttachmentButton
                onUpload={async (newAttachments) => {
                  form.setFieldValue('documents', [
                    ...form.values.documents,
                    ...newAttachments,
                  ])
                  setUploadingStatus('idle')
                }}
                onUploadStart={() => {
                  setUploadingStatus('uploading')
                }}
                onUploadError={() => {
                  setUploadingStatus('error')
                }}
              >
                {(props) => (
                  <div>
                    <Button
                      {...props}
                      loading={uploadingStatus === 'uploading'}
                      size="compact-sm"
                    >
                      Upload{' '}
                      {form.getValues().documents.length > 0
                        ? 'additional'
                        : ''}{' '}
                      documents
                    </Button>
                  </div>
                )}
              </CustomAttachmentButton>
            </div>
            {form.getValues().documents.length > 0 && (
              <>
                <Text className="text-sm text-gray-500">Uploaded docs</Text>
                {form.getValues().documents.map((attachment) => (
                  <div key={attachment.url}>
                    <Anchor target="_blank" href={attachment.url}>
                      {attachment.title}
                    </Anchor>
                  </div>
                ))}
              </>
            )}
          </InputWrapper>
        )}

        <div className="flex grow flex-row items-center justify-end gap-2">
          <Button
            onClick={onCancel}
            variant="outline"
            disabled={isTransactionPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isTransactionPending}
            disabled={isReadOnly}
          >
            Submit
          </Button>
        </div>
        {isTransactionError && (
          <Text className="text-red-500">Failed to create notification</Text>
        )}
      </div>
    </form>
  )
}
